import { useState, useMemo, useCallback, useDeferredValue } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRoleAccess } from '../../users/hooks/useRoleAccess';
import { useInvoices } from '../hooks/useInvoices';
import { Plus } from 'lucide-react';
import {
  getStatusColor,
  NumberUtils,
  DateUtils,
  SearchBar,
  StatusFilters,
  SortableHeader,
  Pagination,
  LoadingState,
  ErrorState,
  EmptyState
} from '@cleanpro/common-ui';
import type { Invoice } from '@cleanpro/common-ui';

/**
 * Invoices component displays a paginated, filterable, and searchable list of invoices.
 *
 * Features:
 * - Fetches and displays invoices with loading and error states.
 * - Allows searching by customer name, invoice number.
 * - Filters invoices by status.
 * - Supports pagination with customizable items per page.
 * - Displays invoice details including invoice number, customer, amount, status, and dates.
 * - Shows status with color-coded badges.
 * - Allows navigation to invoice details and invoice creation (if permitted).
 *
 * @component
 * @returns {JSX.Element} The rendered Invoices table with filters and pagination.
 */
export default function Invoices() {
  const permissions = useRoleAccess();
  const navigate = useNavigate();
  const { invoices, loading, error } = useInvoices();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const deferredSearchTerm = useDeferredValue(searchTerm);

  const ITEMS_PER_PAGE = 25;

  // Filter invoices
  const { filteredInvoices, filterCounts } = useMemo(() => {
    const normalizedSearch = deferredSearchTerm.trim().toLowerCase();
    const counts: Record<'all' | 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled', number> = {
      all: 0,
      draft: 0,
      sent: 0,
      paid: 0,
      overdue: 0,
      cancelled: 0,
    };

    const matches = invoices.filter((invoice) => {
      const haystacks = [
        invoice.customerName || '',
        invoice.invoiceNumber || '',
        invoice.id || ''
      ];

      const matchesSearch =
        normalizedSearch.length === 0 ||
        haystacks.some((value) => value.toLowerCase().includes(normalizedSearch));

      if (!matchesSearch) {
        return false;
      }

      // Count all matches
      counts.all++;
      counts[invoice.status as keyof typeof counts] = (counts[invoice.status as keyof typeof counts] || 0) + 1;

      // Apply status filter
      if (statusFilter !== 'all' && invoice.status !== statusFilter) {
        return false;
      }

      return true;
    });

    return { filteredInvoices: matches, filterCounts: counts };
  }, [invoices, deferredSearchTerm, statusFilter]);

  // Sort invoices
  const sortedInvoices = useMemo(() => {
    const sorted = [...filteredInvoices].sort((a, b) => {
      let aValue: string | number | undefined;
      let bValue: string | number | undefined;

      switch (sortColumn) {
        case 'invoiceNumber':
          aValue = a.invoiceNumber || '';
          bValue = b.invoiceNumber || '';
          break;
        case 'customerName':
          aValue = a.customerName || '';
          bValue = b.customerName || '';
          break;
        case 'amount':
          aValue = a.totalAmount || 0;
          bValue = b.totalAmount || 0;
          break;
        case 'issueDate':
          aValue = a.issueDate;
          bValue = b.issueDate;
          break;
        case 'dueDate':
          aValue = a.dueDate;
          bValue = b.dueDate;
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'createdAt':
        default:
          aValue = a.createdAt || '';
          bValue = b.createdAt || '';
          break;
      }

      if (aValue === undefined || aValue === null) return 1;
      if (bValue === undefined || bValue === null) return -1;

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredInvoices, sortColumn, sortDirection]);

  // Paginate invoices
  const paginatedInvoices = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedInvoices.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [sortedInvoices, currentPage]);

  const totalPages = Math.ceil(sortedInvoices.length / ITEMS_PER_PAGE);

  const handleSort = useCallback((column: string) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  }, [sortColumn]);

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  }, []);

  const handleFilterChange = useCallback((filter: string) => {
    setStatusFilter(filter);
    setCurrentPage(1);
  }, []);

  const handleRowClick = useCallback((invoiceId: string) => {
    navigate(`/invoices/${invoiceId}`);
  }, [navigate]);

  if (loading) {
    return <LoadingState message="Loading invoices..." />;
  }

  if (error) {
    return <ErrorState error={error} />;
  }

  const statusOptions = [
    { value: 'all', label: 'All Invoices', count: filterCounts.all },
    { value: 'draft', label: 'Draft', count: filterCounts.draft },
    { value: 'sent', label: 'Sent', count: filterCounts.sent },
    { value: 'paid', label: 'Paid', count: filterCounts.paid },
    { value: 'overdue', label: 'Overdue', count: filterCounts.overdue },
    { value: 'cancelled', label: 'Cancelled', count: filterCounts.cancelled },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage and track all customer invoices
          </p>
        </div>
        {permissions.canViewFinancial && (
          <button
            onClick={() => navigate('/invoices/create')}
            className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
          >
            <Plus className="h-5 w-5" />
            <span>Create Invoice</span>
          </button>
        )}
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-4">
        <SearchBar
          value={searchTerm}
          onChange={handleSearch}
          placeholder="Search by customer name, invoice number..."
        />
        <StatusFilters
          filters={statusOptions}
          selectedFilter={statusFilter}
          onFilterChange={handleFilterChange}
        />
      </div>

      {/* Invoices Table */}
      {paginatedInvoices.length === 0 ? (
        <EmptyState
          title="No invoices found"
          description={
            statusFilter !== 'all'
              ? 'Try adjusting your filters or search terms'
              : 'Get started by creating your first invoice'
          }
          actionLabel={permissions.canViewFinancial ? 'Create Invoice' : undefined}
          onAction={permissions.canViewFinancial ? () => navigate('/invoices/create') : undefined}
        />
      ) : (
        <>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortableHeader
                      label="Invoice #"
                      column="invoiceNumber"
                      currentColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="Customer"
                      column="customerName"
                      currentColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="Amount"
                      column="amount"
                      currentColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="Issue Date"
                      column="issueDate"
                      currentColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="Due Date"
                      column="dueDate"
                      currentColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="Status"
                      column="status"
                      currentColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedInvoices.map((invoice: Invoice) => (
                    <tr
                      key={invoice.id}
                      onClick={() => handleRowClick(invoice.id)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {invoice.invoiceNumber || `INV-${invoice.id.slice(0, 8)}`}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {invoice.customerName || 'Unknown Customer'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {NumberUtils.formatCurrency(invoice.totalAmount || invoice.amount || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {DateUtils.formatDate(invoice.issueDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {DateUtils.formatDate(invoice.dueDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                            invoice.status,
                            'invoice'
                          )}`}
                        >
                          {invoice.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              totalItems={sortedInvoices.length}
              itemsPerPage={ITEMS_PER_PAGE}
            />
          )}
        </>
      )}
    </div>
  );
}
